import { z } from "zod";

/** Aligned with `prompt1` output contract in reportPrompts.ts */
export const MODEL1_TEMPLATE_SCHEMA_VERSION = "1.0" as const;

export const fieldTypeZ = z.enum([
    "string",
    "number",
    "integer",
    "boolean",
    "date",
    "datetime",
    "array",
    "object",
    "table"
]);

export const tableColumnSchema = z.object({
    column_name: z.string().min(1),
    description: z.string(),
    type: z.enum(["string", "number", "integer", "boolean", "date", "datetime"]),
    nullable: z.boolean()
});

export type Model1TableColumn = z.infer<typeof tableColumnSchema>;

export type Model1FieldDefinition = {
    field_name: string;
    description: string;
    type: z.infer<typeof fieldTypeZ>;
    required: boolean;
    nullable: boolean;
    enum_values?: string[];
    item_type?: string;
    table_columns?: Model1TableColumn[];
    properties?: Model1FieldDefinition[];
};

export const fieldDefinitionSchema: z.ZodType<Model1FieldDefinition> = z.lazy(() =>
    z.object({
        field_name: z.string().min(1),
        description: z.string(),
        type: fieldTypeZ,
        required: z.boolean(),
        nullable: z.boolean(),
        enum_values: z.array(z.string()).optional(),
        item_type: z.string().optional(),
        table_columns: z.array(tableColumnSchema).optional(),
        properties: z.array(fieldDefinitionSchema).optional()
    })
);

export const model1TemplateV1Schema = z
    .object({
        document_type: z.string(),
        document_description: z.string(),
        schema_version: z.literal(MODEL1_TEMPLATE_SCHEMA_VERSION),
        extraction_instructions: z.object({
            overall_goal: z.string(),
            notes: z.array(z.string())
        }),
        fields: z.array(fieldDefinitionSchema).min(1)
    })
    .strict()
    .superRefine((data, ctx) => {
        const visit = (fields: Model1FieldDefinition[], basePath: (string | number)[]) => {
            const names = new Set<string>();
            fields.forEach((f, i) => {
                const pathSeg = [...basePath, i];
                if (names.has(f.field_name)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `duplicate_field_name:${f.field_name}`,
                        path: [...pathSeg, "field_name"]
                    });
                }
                names.add(f.field_name);

                if (f.type === "table" && (!f.table_columns || f.table_columns.length === 0)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: "table_requires_columns",
                        path: [...pathSeg, "table_columns"]
                    });
                }

                if (f.type === "object" && f.properties && f.properties.length > 0) {
                    visit(f.properties, [...pathSeg, "properties"]);
                }
                if (f.type === "array" && f.item_type === "object" && f.properties && f.properties.length > 0) {
                    visit(f.properties, [...pathSeg, "properties"]);
                }
            });
        };
        visit(data.fields, ["fields"]);
    });

export type Model1TemplateV1 = z.infer<typeof model1TemplateV1Schema>;
